import { useSearchController } from 'controllers/SearchController/SearchContext';
import useHasFilterApplied from 'components/Filters/context/SearchBarContext/hooks/useHasFilterApplied';
import { usePaneResults } from 'components/DetailsView/components/PaneResults';
import {
    EmojiOutlined,
    GuitarOutlined,
    KeyboardOutlined,
    MapPinOutlined,
    MusicNote,
    NoteOutlined,
    PriceTagOutlined,
    SunOutlined,
    UserOutlined,
} from '@landr/maestro';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import React, { useCallback, useMemo, useState } from 'react';
import { t } from '@lingui/macro';
import { useSearchBarContext } from 'components/Filters/context/SearchBarContext/SearchBarContext';
import { FiltersParams, TagCategoryParams } from 'types/params';
import { TagCategory } from 'types/generated/samplesApi';
import { useFlag } from '@landr/mfe';
import { FeatureFlags } from '@landr/root-app/feature-flags';
import { useFilterVisibility } from './useFilterVisibility';

const useAdvancedFilter = () => {
    const { samplesTotal } = useSearchController();
    const { handleClearCta } = useSearchBarContext();
    const hasFilterApplied = useHasFilterApplied({ includeType: true });
    const { resultsCopy } = usePaneResults({
        total: samplesTotal,
        showNoFilterCopy: false,
        hasFilterApplied,
    });
    const isAdvancedFiltersV3 = useFlag(FeatureFlags.AdvancedFiltersV3);
    const isAdvancedFiltersV3Full = useFlag(FeatureFlags.AdvancedFiltersV3Full);

    const {
        bpmlowerbound,
        bpmupperbound,
        instruments: instrumentsState,
        genres: genresState,
        sfxs: sfxsState,
        tags: tagsState,
        keyquality,
        keyrootnote,
    } = useCurrentSearchState();

    const bpmApplied = bpmlowerbound || bpmupperbound ? 1 : 0;
    const genresApplied = genresState?.length ?? 0;
    const instrumentsApplied = instrumentsState?.length ?? 0;
    const keyNoteApplied = keyrootnote ? 1 : 0;
    const keyQualityApplied = keyquality ? 1 : 0;
    const sfxsApplied = sfxsState?.length ?? 0;
    const tagsApplied = tagsState?.length ?? 0;

    const filtersAppliedTotal = [
        bpmApplied,
        genresApplied,
        instrumentsApplied,
        keyNoteApplied,
        keyQualityApplied,
        sfxsApplied,
        tagsApplied,
    ].reduce((partialSum, a) => partialSum + a, 0);

    const {
        instruments,
        genres,
        sfxs,
        artistsTags,
        timbreTags,
        moodTags,
        cityTags,
        musicGearTags,
        otherTags,
    } = useFilterVisibility();
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

    const availableAdvancedFilters = [
        FiltersParams.instruments,
        FiltersParams.genres,
        FiltersParams.sfxs,
    ];

    const availableAdvancedFiltersV3 = [
        FiltersParams.instruments,
        FiltersParams.genres,
        FiltersParams.sfxs,
        TagCategoryParams.artist,
        TagCategoryParams.timbre,
        TagCategoryParams.mood,
    ];

    const availableAdvancedFiltersV3Full = [
        FiltersParams.instruments,
        FiltersParams.genres,
        FiltersParams.sfxs,
        TagCategoryParams.artist,
        TagCategoryParams.timbre,
        TagCategoryParams.mood,
        TagCategoryParams.city,
        TagCategoryParams.music_gear,
        TagCategoryParams.other,
    ];

    let activeAdvancedFilters: (FiltersParams | TagCategoryParams)[] =
        availableAdvancedFilters;

    if (isAdvancedFiltersV3Full) {
        activeAdvancedFilters = availableAdvancedFiltersV3Full;
    } else if (isAdvancedFiltersV3) {
        activeAdvancedFilters = availableAdvancedFiltersV3;
    }

    const getFilterState = useCallback(
        (filter: FiltersParams) => {
            if (filter === FiltersParams.instruments) {
                return instrumentsState;
            } else if (filter === FiltersParams.genres) {
                return genresState;
            } else if (filter === FiltersParams.sfxs) {
                return sfxsState;
            } else if (filter === FiltersParams.tags) {
                return tagsState;
            } else {
                return [];
            }
        },
        [genresState, instrumentsState, sfxsState, tagsState],
    );

    const getFilterResults = useCallback(
        (filter: FiltersParams | TagCategoryParams) => {
            if (filter === FiltersParams.instruments) {
                return instruments;
            } else if (filter === FiltersParams.genres) {
                return genres;
            } else if (filter === FiltersParams.sfxs) {
                return sfxs;
            } else if (filter === TagCategoryParams.artist) {
                return artistsTags;
            } else if (filter === TagCategoryParams.timbre) {
                return timbreTags;
            } else if (filter === TagCategoryParams.mood) {
                return moodTags;
            } else if (filter === TagCategoryParams.city) {
                return cityTags;
            } else if (filter === TagCategoryParams.music_gear) {
                return musicGearTags;
            } else if (filter === TagCategoryParams.other) {
                return otherTags;
            } else {
                return [];
            }
        },
        [
            instruments,
            genres,
            sfxs,
            artistsTags,
            timbreTags,
            moodTags,
            cityTags,
            musicGearTags,
            otherTags,
        ],
    );

    const getFilterIcon = useCallback(
        (filter: FiltersParams | TagCategoryParams) => {
            if (filter === FiltersParams.instruments) {
                return <GuitarOutlined />;
            } else if (filter === FiltersParams.genres) {
                return <MusicNote />;
            } else if (filter === FiltersParams.sfxs) {
                return <SunOutlined />;
            } else if (filter === TagCategoryParams.artist) {
                return <UserOutlined />;
            } else if (filter === TagCategoryParams.timbre) {
                return <NoteOutlined />;
            } else if (filter === TagCategoryParams.mood) {
                return <EmojiOutlined />;
            } else if (filter === TagCategoryParams.city) {
                return <MapPinOutlined />;
            } else if (filter === TagCategoryParams.music_gear) {
                return <KeyboardOutlined />;
            } else if (filter === TagCategoryParams.other) {
                return <PriceTagOutlined />;
            } else {
                return <></>;
            }
        },
        [],
    );

    const getFilterName = useCallback(
        (filter: FiltersParams | TagCategoryParams) => {
            if (filter === FiltersParams.instruments) {
                return t`Instruments`;
            } else if (filter === FiltersParams.genres) {
                return t`Genres`;
            } else if (filter === FiltersParams.sfxs) {
                return t`SFX`;
            } else if (filter === TagCategoryParams.artist) {
                return t`Artists`;
            } else if (filter === TagCategoryParams.timbre) {
                return t`Timbres`;
            } else if (filter === TagCategoryParams.mood) {
                return t`Moods`;
            } else if (filter === TagCategoryParams.city) {
                return t`Cities`;
            } else if (filter === TagCategoryParams.music_gear) {
                return t`Music Gear`;
            } else if (filter === TagCategoryParams.other) {
                return t`Others`;
            } else {
                return '';
            }
        },
        [],
    );

    const getTagsCategoryState = useCallback(
        (TagCategory: TagCategory) => {
            if (TagCategory === TagCategoryParams.artist) {
                return artistsTags?.filter((tag) =>
                    tagsState?.includes(tag.code),
                );
            } else if (TagCategory === TagCategoryParams.timbre) {
                return timbreTags?.filter((tag) =>
                    tagsState?.includes(tag.code),
                );
            } else if (TagCategory === TagCategoryParams.mood) {
                return moodTags?.filter((tag) => tagsState?.includes(tag.code));
            } else if (TagCategory === TagCategoryParams.city) {
                return cityTags?.filter((tag) => tagsState?.includes(tag.code));
            } else if (TagCategory === TagCategoryParams.music_gear) {
                return musicGearTags?.filter((tag) =>
                    tagsState?.includes(tag.code),
                );
            } else if (TagCategory === TagCategoryParams.other) {
                return otherTags?.filter((tag) =>
                    tagsState?.includes(tag.code),
                );
            } else {
                return [];
            }
        },
        [
            artistsTags,
            tagsState,
            timbreTags,
            moodTags,
            cityTags,
            musicGearTags,
            otherTags,
        ],
    );

    const getIsTagsCategory = useCallback(
        (filter: FiltersParams | TagCategoryParams) => {
            return (
                filter === TagCategoryParams.artist ||
                filter === TagCategoryParams.timbre ||
                filter === TagCategoryParams.mood ||
                filter === TagCategoryParams.city ||
                filter === TagCategoryParams.music_gear ||
                filter === TagCategoryParams.other
            );
        },
        [],
    );

    const setIsAdvancedFiltersMobileOpen = useCallback(
        (isOpen: boolean) => {
            setIsMobileFilterOpen(isOpen);
        },
        [setIsMobileFilterOpen],
    );

    const getIsAdvancedFiltersMobileOpen = useCallback(() => {
        return isMobileFilterOpen;
    }, [isMobileFilterOpen]);

    const noAdvancedFiltersAvailable = useMemo(() => {
        return !instruments.length && !genres.length && !sfxs.length;
    }, [instruments, genres, sfxs]);

    return {
        artistsTags,
        availableAdvancedFilters: activeAdvancedFilters,
        cityTags,
        filtersAppliedTotal,
        genresState,
        instrumentsState,
        moodTags,
        musicGearTags,
        noAdvancedFiltersAvailable,
        otherTags,
        resultsCopy,
        sfxsState,
        timbreTags,
        getFilterIcon,
        getFilterName,
        getFilterResults,
        getFilterState,
        getIsAdvancedFiltersMobileOpen,
        getIsTagsCategory,
        getTagsCategoryState,
        handleClearCta,
        setIsAdvancedFiltersMobileOpen,
    };
};

export default useAdvancedFilter;
